<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title class="primary white--text">
              <v-icon left dark>mdi-bug</v-icon>
              Consultation Billing Debug Console
              <v-spacer></v-spacer>
              <v-chip :color="isConnected ? 'green' : 'red'" dark small>
                <v-icon left small>{{
                  isConnected ? "mdi-wifi" : "mdi-wifi-off"
                }}</v-icon>
                {{
                  isConnected ? "WebSocket Connected" : "WebSocket Disconnected"
                }}
              </v-chip>
            </v-card-title>

            <!-- Control Panel -->
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-subtitle>Control Panel</v-card-subtitle>
                    <v-card-text>
                      <v-btn
                        @click="fetchActiveBillings"
                        color="primary"
                        block
                        class="mb-2"
                        :loading="loading"
                      >
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh Active Billings
                      </v-btn>
                      <v-btn
                        @click="fetchPendingConsultations"
                        color="info"
                        block
                        class="mb-2"
                        :loading="loadingPending"
                      >
                        <v-icon left>mdi-clock-alert-outline</v-icon>
                        Refresh Pending Approval
                      </v-btn>
                      <v-btn
                        @click="fetchApprovedConsultations"
                        color="success"
                        block
                        class="mb-2"
                        :loading="loadingApproved"
                      >
                        <v-icon left>mdi-check-circle-outline</v-icon>
                        Refresh Approved Consultations
                      </v-btn>
                      <v-btn
                        @click="startBillingJob"
                        color="success"
                        block
                        class="mb-2"
                      >
                        <v-icon left>mdi-play</v-icon>
                        Start Billing Job
                      </v-btn>
                      <v-btn
                        @click="clearLogs"
                        color="warning"
                        block
                        class="mb-2"
                      >
                        <v-icon left>mdi-delete</v-icon>
                        Clear Logs
                      </v-btn>
                      <v-btn @click="checkQueueStatus" color="info" block>
                        <v-icon left>mdi-queue-first-in-last-out</v-icon>
                        Check Queue Status
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-subtitle>System Status</v-card-subtitle>
                    <v-card-text>
                      <div
                        class="d-flex justify-space-between align-center mb-2"
                      >
                        <span>Active Billing Sessions:</span>
                        <v-chip color="primary">{{
                          activeBillings.length
                        }}</v-chip>
                      </div>
                      <div
                        class="d-flex justify-space-between align-center mb-2"
                      >
                        <span>Pending Approval:</span>
                        <v-chip color="orange">{{
                          pendingConsultations.length
                        }}</v-chip>
                      </div>
                      <div
                        class="d-flex justify-space-between align-center mb-2"
                      >
                        <span>Approved Consultations:</span>
                        <v-chip color="green">{{
                          approvedConsultations.length
                        }}</v-chip>
                      </div>
                      <div
                        class="d-flex justify-space-between align-center mb-2"
                      >
                        <span>WebSocket Events Received:</span>
                        <v-chip color="green">{{
                          websocketEvents.length
                        }}</v-chip>
                      </div>
                      <div
                        class="d-flex justify-space-between align-center mb-2"
                      >
                        <span>Last Update:</span>
                        <small>{{ lastUpdate || "Never" }}</small>
                      </div>
                      <div class="d-flex justify-space-between align-center">
                        <span>Queue Job Status:</span>
                        <v-chip :color="queueStatus.color">{{
                          queueStatus.text
                        }}</v-chip>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Active Billing Sessions -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-cash-clock</v-icon>
              Active Billing Sessions
              <v-spacer></v-spacer>
              <v-btn small @click="fetchActiveBillings" :loading="loading">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>

            <v-data-table
              :headers="billingHeaders"
              :items="activeBillings"
              :loading="loading"
              class="elevation-1"
            >
              <template v-slot:[`item.patient_name`]="{ item }">
                <div>
                  <strong>{{ item.patient_name }}</strong>
                  <br />
                  <small>Wallet: ₹{{ item.patient_wallet }}</small>
                </div>
              </template>

              <template v-slot:[`item.doctor_name`]="{ item }">
                <div>
                  <strong>{{ item.doctor_name }}</strong>
                  <br />
                  <small>Rate: ₹{{ item.rate_per_minute }}/min</small>
                </div>
              </template>

              <template v-slot:[`item.billing_info`]="{ item }">
                <div>
                  <v-chip small color="success" class="mb-1"
                    >{{ item.total_minutes_billed }} min</v-chip
                  >
                  <br />
                  <v-chip small color="primary"
                    >₹{{ item.total_amount_deducted }}</v-chip
                  >
                </div>
              </template>

              <template v-slot:[`item.last_deduction`]="{ item }">
                <div>
                  <small>{{ formatTime(item.last_deduction_at) }}</small>
                  <br />
                  <status-chip x-small :color="getTimeSinceColor(item.last_deduction_at)" :label="String(getTimeSince(item.last_deduction_at))" />
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  small
                  color="error"
                  @click="stopBilling(item.consultation_id)"
                  class="mr-1"
                >
                  Stop
                </v-btn>
                <v-btn
                  small
                  color="warning"
                  @click="forceBilling(item.consultation_id)"
                >
                  Force Bill
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Pending Consultations (Status 0 - Need Approval) -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-clock-alert-outline</v-icon>
              Pending Approval (Status 0)
              <v-spacer></v-spacer>
              <v-btn small @click="fetchPendingConsultations" :loading="loadingPending">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>

            <v-data-table
              :headers="pendingHeaders"
              :items="pendingConsultations"
              :loading="loadingPending"
              class="elevation-1"
              no-data-text="No consultations pending approval"
            >
              <template v-slot:[`item.patient_info`]="{ item }">
                <div>
                  <strong>{{ item.patient_name }}</strong>
                  <br />
                  <small>Wallet: ₹{{ item.patient_wallet }}</small>
                </div>
              </template>

              <template v-slot:[`item.doctor_info`]="{ item }">
                <div>
                  <strong>{{ item.doctor_name }}</strong>
                  <br />
                  <small>Rate: ₹{{ item.doctor_rate_per_minute }}/min</small>
                </div>
              </template>

              <template v-slot:[`item.approval_status`]="{ item }">
                <div>
                  <v-chip 
                    x-small 
                    :color="item.approval_readiness.doctor_rate_set ? 'green' : 'red'" 
                    dark
                    class="mb-1 mr-1"
                  >
                    Rate {{ item.approval_readiness.doctor_rate_set ? 'Set' : 'Missing' }}
                  </v-chip>
                  <br />
                  <v-chip 
                    x-small 
                    :color="item.approval_readiness.sufficient_funds ? 'green' : 'orange'" 
                    dark
                    class="mb-1 mr-1"
                  >
                    Funds {{ item.approval_readiness.sufficient_funds ? 'OK' : 'Low' }}
                  </v-chip>
                  <div v-if="item.approval_readiness.funds_shortage > 0" class="caption red--text">
                    Shortage: ₹{{ item.approval_readiness.funds_shortage }}
                  </div>
                </div>
              </template>

              <template v-slot:[`item.approval_actions`]="{ item }">
                <v-btn 
                  small 
                  color="success" 
                  @click="approveConsultation(item.consultation_id)"
                  :disabled="!item.can_approve"
                >
                  <v-icon small left>mdi-check</v-icon>
                  Approve
                </v-btn>
              </template>

              <template v-slot:[`item.can_approve`]="{ item }">
                <status-chip :color="item.can_approve ? 'green' : 'red'" :label="String(item.can_approve ? 'Can Approve' : 'Not Ready')" />
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Approved Consultations (Status 1 - Ready for Billing) -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-check-circle-outline</v-icon>
              Approved Consultations (Status 1)
              <v-spacer></v-spacer>
              <v-btn small @click="fetchApprovedConsultations" :loading="loadingApproved">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>

            <v-data-table
              :headers="approvedHeaders"
              :items="approvedConsultations"
              :loading="loadingApproved"
              class="elevation-1"
              no-data-text="No approved consultations ready for billing"
            >
              <template v-slot:[`item.patient_info`]="{ item }">
                <div>
                  <strong>{{ item.patient_name }}</strong>
                  <br />
                  <small>Wallet: ₹{{ item.patient_wallet }}</small>
                </div>
              </template>

              <template v-slot:[`item.doctor_info`]="{ item }">
                <div>
                  <strong>{{ item.doctor_name }}</strong>
                  <br />
                  <small>Rate: ₹{{ item.doctor_rate_per_minute }}/min</small>
                </div>
              </template>

              <template v-slot:[`item.billing_readiness`]="{ item }">
                <div>
                  <v-chip 
                    x-small 
                    :color="item.billing_readiness.patient_joined ? 'green' : 'red'" 
                    dark
                    class="mb-1 mr-1"
                  >
                    Patient {{ item.billing_readiness.patient_joined ? 'Joined' : 'Not Joined' }}
                  </v-chip>
                  <br />
                  <v-chip 
                    x-small 
                    :color="item.billing_readiness.doctor_joined ? 'green' : 'red'" 
                    dark
                    class="mb-1 mr-1"
                  >
                    Doctor {{ item.billing_readiness.doctor_joined ? 'Joined' : 'Not Joined' }}
                  </v-chip>
                  <br />
                  <v-chip 
                    x-small 
                    :color="item.billing_readiness.doctor_rate_set ? 'green' : 'red'" 
                    dark
                    class="mb-1 mr-1"
                  >
                    Rate {{ item.billing_readiness.doctor_rate_set ? 'Set' : 'Not Set' }}
                  </v-chip>
                  <br />
                  <v-chip 
                    x-small 
                    :color="item.billing_readiness.sufficient_funds ? 'green' : 'orange'" 
                    dark
                    class="mb-1 mr-1"
                  >
                    Funds {{ item.billing_readiness.sufficient_funds ? 'OK' : 'Low' }}
                  </v-chip>
                  <div v-if="item.billing_readiness.funds_shortage > 0" class="caption red--text">
                    Shortage: ₹{{ item.billing_readiness.funds_shortage }}
                  </div>
                </div>
              </template>

              <template v-slot:[`item.join_actions`]="{ item }">
                <div>
                  <v-btn 
                    x-small 
                    color="primary" 
                    @click="updateConsultationStatus(item.consultation_id, 'join_patient')"
                    :disabled="item.billing_readiness.patient_joined"
                    class="mb-1 mr-1"
                  >
                    Patient Join
                  </v-btn>
                  <br />
                  <v-btn 
                    x-small 
                    color="primary" 
                    @click="updateConsultationStatus(item.consultation_id, 'join_doctor')"
                    :disabled="item.billing_readiness.doctor_joined"
                    class="mb-1 mr-1"
                  >
                    Doctor Join
                  </v-btn>
                </div>
              </template>

              <template v-slot:[`item.billing_actions`]="{ item }">
                <v-btn 
                  small 
                  color="success" 
                  @click="startBillingForConsultation(item.consultation_id)"
                  :disabled="!item.can_start_billing"
                >
                  <v-icon small left>mdi-play</v-icon>
                  Start Billing
                </v-btn>
              </template>

              <template v-slot:[`item.can_start_billing`]="{ item }">
                <status-chip :color="item.can_start_billing ? 'green' : 'red'" :label="String(item.can_start_billing ? 'Ready' : 'Not Ready')" />
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Real-time WebSocket Events -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-flash</v-icon>
              Real-time WebSocket Events
              <v-spacer></v-spacer>
              <v-switch
                v-model="autoScroll"
                label="Auto Scroll"
                dense
                hide-details
              ></v-switch>
            </v-card-title>

            <v-card-text
              style="max-height: 400px; overflow-y: auto"
              ref="eventsContainer"
            >
              <div
                v-if="websocketEvents.length === 0"
                class="text-center grey--text"
              >
                No WebSocket events received yet. Start a billing session to see
                real-time updates.
              </div>

              <v-timeline dense v-else>
                <v-timeline-item
                  v-for="(event, index) in websocketEvents.slice().reverse()"
                  :key="index"
                  :color="getEventColor(event.type)"
                  small
                >
                  <template #icon>
                    <v-icon small dark>{{ getEventIcon(event.type) }}</v-icon>
                  </template>

                  <v-card elevation="2" class="mb-2">
                    <v-card-subtitle class="pb-1">
                      <strong>{{ event.type }}</strong>
                      <small class="float-right">{{
                        formatTime(event.timestamp)
                      }}</small>
                    </v-card-subtitle>
                    <v-card-text class="pt-0">
                      <pre class="caption">{{
                        JSON.stringify(event.data, null, 2)
                      }}</pre>
                    </v-card-text>
                  </v-card>
                </v-timeline-item>
              </v-timeline>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Debug Logs -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-console</v-icon>
              Debug Logs
              <v-spacer></v-spacer>
              <v-btn small @click="downloadLogs">
                <v-icon small left>mdi-download</v-icon>
                Download
              </v-btn>
            </v-card-title>

            <v-card-text
              style="
                max-height: 300px;
                overflow-y: auto;
                background-color: #1e1e1e;
                color: #fff;
              "
              ref="logsContainer"
            >
              <div
                v-for="(log, index) in debugLogs"
                :key="index"
                class="font-weight-mono caption"
              >
                <span :class="getLogColor(log.level)"
                  >[{{ formatTime(log.timestamp) }}]</span
                >
                <span :class="getLogColor(log.level)"
                  >[{{ log.level.toUpperCase() }}]</span
                >
                {{ log.message }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Billing Test Controls -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-test-tube</v-icon>
              Billing Test Controls
            </v-card-title>

            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="testConsultationId"
                    :items="consultationOptions"
                    label="Select Consultation"
                    item-text="text"
                    item-value="value"
                    outlined
                    dense
                  ></v-select>
                </v-col>
                <v-col cols="12" md="8">
                  <v-btn
                    @click="startTestBilling"
                    color="success"
                    class="mr-2"
                    :disabled="!testConsultationId"
                  >
                    Start Test Billing
                  </v-btn>
                  <v-btn
                    @click="stopTestBilling"
                    color="error"
                    class="mr-2"
                    :disabled="!testConsultationId"
                  >
                    Stop Test Billing
                  </v-btn>
                  <v-btn
                    @click="getBillingStatus"
                    color="info"
                    :disabled="!testConsultationId"
                  >
                    Check Status
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Snackbar for notifications -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        timeout="5000"
      >
        {{ snackbar.message }}
        <template #action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false"
            >Close</v-btn
          >
        </template>
      </v-snackbar>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import Echo from "laravel-echo";
import Pusher from "pusher-js";

export default {
  name: "BillingDebug",
  data() {
    return {
      loading: false,
      loadingPending: false,
      loadingApproved: false,
      isConnected: false,
      autoScroll: true,
      lastUpdate: null,
      testConsultationId: null,

      activeBillings: [],
      pendingConsultations: [],
      approvedConsultations: [],
      websocketEvents: [],
      debugLogs: [],

      queueStatus: {
        text: "Unknown",
        color: "grey",
      },

      snackbar: {
        show: false,
        message: "",
        color: "info",
      },

      billingHeaders: [
        { text: "Consultation ID", value: "consultation_id", sortable: true },
        { text: "Patient", value: "patient_name", sortable: false },
        { text: "Doctor", value: "doctor_name", sortable: false },
        { text: "Billing Info", value: "billing_info", sortable: false },
        { text: "Last Deduction", value: "last_deduction", sortable: true },
        { text: "Actions", value: "actions", sortable: false },
      ],

      pendingHeaders: [
        { text: "ID", value: "consultation_id", sortable: true },
        { text: "Title", value: "title", sortable: false },
        { text: "Patient", value: "patient_info", sortable: false },
        { text: "Doctor", value: "doctor_info", sortable: false },
        { text: "Status", value: "approval_status", sortable: false },
        { text: "Can Approve", value: "can_approve", sortable: false },
        { text: "Actions", value: "approval_actions", sortable: false },
      ],

      approvedHeaders: [
        { text: "ID", value: "consultation_id", sortable: true },
        { text: "Title", value: "title", sortable: false },
        { text: "Patient", value: "patient_info", sortable: false },
        { text: "Doctor", value: "doctor_info", sortable: false },
        { text: "Billing Ready", value: "billing_readiness", sortable: false },
        { text: "Join Actions", value: "join_actions", sortable: false },
        { text: "Can Start", value: "can_start_billing", sortable: false },
        { text: "Billing", value: "billing_actions", sortable: false },
      ],

      refreshInterval: null,
      echo: null,
    };
  },

  computed: {
    consultationOptions() {
      return this.activeBillings.map((billing) => ({
        text: `Consultation ${billing.consultation_id} - ${billing.patient_name} & ${billing.doctor_name}`,
        value: billing.consultation_id,
      }));
    },
  },

  mounted() {
    this.initializeWebSocket();
    this.fetchActiveBillings();
    this.fetchPendingConsultations();
    this.fetchApprovedConsultations();
    this.setupRefreshInterval();
    this.addLog("info", "Billing Debug Console initialized");
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.echo) {
      this.echo.disconnect();
    }
  },

  methods: {
    // WebSocket initialization
    initializeWebSocket() {
      try {
        // Check if environment variables are properly set
        if (!process.env.VUE_APP_REVERB_APP_KEY) {
          this.addLog("warning", "REVERB_APP_KEY not set in environment variables");
          return;
        }

        window.Pusher = Pusher;

        this.echo = new Echo({
          broadcaster: "reverb",
          key: process.env.VUE_APP_REVERB_APP_KEY,
          wsHost: process.env.VUE_APP_REVERB_HOST || "127.0.0.1",
          wsPort: process.env.VUE_APP_REVERB_PORT || 6001,
          wssPort: process.env.VUE_APP_REVERB_PORT || 6001,
          forceTLS: false, // Set to false for local development
          enabledTransports: ["ws", "wss"],
          auth: {
            headers: {
              Authorization: `Bearer ${this.$store.getters["auth/token"]}`,
            },
          },
        });

        if (this.echo && this.echo.connector && this.echo.connector.pusher) {
          this.echo.connector.pusher.connection.bind("connected", () => {
            this.isConnected = true;
            this.addLog("success", "WebSocket connected successfully");
          });

          this.echo.connector.pusher.connection.bind("disconnected", () => {
            this.isConnected = false;
            this.addLog("error", "WebSocket disconnected");
          });

          this.echo.connector.pusher.connection.bind("error", (error) => {
            this.addLog(
              "error",
              `WebSocket error: ${error?.message || "Unknown error"}`
            );
          });

          // Listen for billing events
          this.setupBillingEventListeners();
        } else {
          this.addLog("error", "Failed to initialize Echo connector");
        }
      } catch (error) {
        this.addLog(
          "error",
          `Failed to initialize WebSocket: ${error?.message || "Unknown error"}`
        );
      }
    },

    setupBillingEventListeners() {
      // Skip if Echo is not properly initialized
      if (!this.echo || !this.echo.private) {
        this.addLog("warning", "Echo not initialized, skipping event listeners");
        return;
      }

      try {
        // Listen for billing minute processed events
        this.activeBillings.forEach((billing) => {
          if (billing.consultation_id && billing.patient_id) {
            this.echo
              .private(`billing.${billing.consultation_id}`)
              .listen(".billing.minute.processed", (data) => {
                this.handleWebSocketEvent("billing.minute.processed", data);
              });

            this.echo
              .private(`user.${billing.patient_id}`)
              .listen(".billing.minute.processed", (data) => {
                this.handleWebSocketEvent("billing.minute.processed", data);
              })
              .listen(".billing.session.stopped", (data) => {
                this.handleWebSocketEvent("billing.session.stopped", data);
              })
              .listen(".billing.insufficient.funds", (data) => {
                this.handleWebSocketEvent("billing.insufficient.funds", data);
              });
          }
        });
        this.addLog("info", `Set up WebSocket listeners for ${this.activeBillings.length} billing sessions`);
      } catch (error) {
        this.addLog("error", `Failed to set up WebSocket listeners: ${error?.message || "Unknown error"}`);
      }
    },

    handleWebSocketEvent(type, data) {
      this.websocketEvents.push({
        type,
        data,
        timestamp: new Date(),
      });

      this.addLog("info", `WebSocket Event: ${type}`);
      this.fetchActiveBillings(); // Refresh data when events are received

      if (this.autoScroll && this.$refs.eventsContainer) {
        this.$nextTick(() => {
          this.$refs.eventsContainer.scrollTop =
            this.$refs.eventsContainer.scrollHeight;
        });
      }
    },

    // API calls
    async fetchActiveBillings() {
      this.loading = true;
      try {
        const response = await axios.get("/consultation_billings/active");
        this.activeBillings = response.data.data || [];
        this.lastUpdate = new Date().toLocaleTimeString();
        this.addLog(
          "success",
          `Fetched ${this.activeBillings.length} active billing sessions`
        );

        // Refresh WebSocket listeners
        this.setupBillingEventListeners();
      } catch (error) {
        this.addLog(
          "error",
          `Failed to fetch active billings: ${error.message}`
        );
        this.showSnackbar("Failed to fetch active billings", "error");
      } finally {
        this.loading = false;
      }
    },

    async startBillingJob() {
      try {
        await axios.post("/consultation_billings/start_job");
        this.addLog("success", "Billing job started successfully");
        this.showSnackbar("Billing job started", "success");
        this.queueStatus = { text: "Running", color: "green" };
      } catch (error) {
        this.addLog("error", `Failed to start billing job: ${error.message}`);
        this.showSnackbar("Failed to start billing job", "error");
      }
    },

    async checkQueueStatus() {
      try {
        const response = await axios.get(
          "/consultation_billings/queue_status"
        );
        const data = response.data.data;

        this.queueStatus = {
          text: data.queue_status,
          color: data.queue_status === "processing" ? "green" : "orange",
        };

        this.addLog(
          "success",
          `Queue Status: ${data.queue_status} - ${data.pending_jobs} pending, ${data.failed_jobs} failed, ${data.active_billings} active billings`
        );
        this.showSnackbar("Queue status retrieved", "info");
      } catch (error) {
        this.addLog("error", `Failed to check queue status: ${error.message}`);
        this.showSnackbar("Failed to check queue status", "error");
      }
    },

    async stopBilling(consultationId) {
      try {
        await axios.post("/consultation_billings/stop", {
          consultation_id: consultationId,
        });
        this.addLog(
          "success",
          `Stopped billing for consultation ${consultationId}`
        );
        this.showSnackbar("Billing stopped", "success");
        this.fetchActiveBillings();
      } catch (error) {
        this.addLog("error", `Failed to stop billing: ${error.message}`);
        this.showSnackbar("Failed to stop billing", "error");
      }
    },

    async forceBilling(consultationId) {
      try {
        await axios.post("/consultation_billings/process_billing", {
          consultation_id: consultationId,
        });
        this.addLog(
          "success",
          `Force processed billing for consultation ${consultationId}`
        );
        this.showSnackbar("Billing processed", "success");
        this.fetchActiveBillings();
      } catch (error) {
        this.addLog("error", `Failed to force billing: ${error.message}`);
        this.showSnackbar("Failed to process billing", "error");
      }
    },

    async startTestBilling() {
      try {
        await axios.post("/consultation_billings/start", {
          consultation_id: this.testConsultationId,
        });
        this.addLog(
          "success",
          `Started test billing for consultation ${this.testConsultationId}`
        );
        this.showSnackbar("Test billing started", "success");
        this.fetchActiveBillings();
      } catch (error) {
        this.addLog("error", `Failed to start test billing: ${error.message}`);
        this.showSnackbar("Failed to start test billing", "error");
      }
    },

    async stopTestBilling() {
      try {
        await axios.post("/consultation_billings/stop", {
          consultation_id: this.testConsultationId,
        });
        this.addLog(
          "success",
          `Stopped test billing for consultation ${this.testConsultationId}`
        );
        this.showSnackbar("Test billing stopped", "success");
        this.fetchActiveBillings();
      } catch (error) {
        this.addLog("error", `Failed to stop test billing: ${error.message}`);
        this.showSnackbar("Failed to stop test billing", "error");
      }
    },

    async getBillingStatus() {
      try {
        const response = await axios.get("/consultation_billings/summary", {
          params: { consultation_id: this.testConsultationId },
        });
        this.addLog(
          "info",
          `Billing status: ${JSON.stringify(response.data.data)}`
        );
        this.showSnackbar("Billing status retrieved", "info");
      } catch (error) {
        this.addLog("error", `Failed to get billing status: ${error.message}`);
        this.showSnackbar("Failed to get billing status", "error");
      }
    },

    // Consultation Lifecycle Methods
    async fetchPendingConsultations() {
      this.loadingPending = true;
      try {
        const response = await axios.get("/consultation_billings/pending_consultations");
        this.pendingConsultations = response.data.data || [];
        this.addLog(
          "success",
          `Fetched ${this.pendingConsultations.length} consultations pending approval`
        );
      } catch (error) {
        this.addLog(
          "error",
          `Failed to fetch pending consultations: ${error.message}`
        );
        this.showSnackbar("Failed to fetch pending consultations", "error");
      } finally {
        this.loadingPending = false;
      }
    },

    async fetchApprovedConsultations() {
      this.loadingApproved = true;
      try {
        const response = await axios.get("/consultation_billings/approved_consultations");
        this.approvedConsultations = response.data.data || [];
        this.addLog(
          "success",
          `Fetched ${this.approvedConsultations.length} approved consultations ready for billing`
        );
      } catch (error) {
        this.addLog(
          "error",
          `Failed to fetch approved consultations: ${error.message}`
        );
        this.showSnackbar("Failed to fetch approved consultations", "error");
      } finally {
        this.loadingApproved = false;
      }
    },

    async approveConsultation(consultationId) {
      try {
        await axios.post("/consultation_billings/approve_consultation", {
          consultation_id: consultationId
        });
        this.addLog(
          "success",
          `Approved consultation ${consultationId}`
        );
        this.showSnackbar("Consultation approved successfully", "success");
        // Refresh both lists since this consultation will move from pending to approved
        this.fetchPendingConsultations();
        this.fetchApprovedConsultations();
      } catch (error) {
        this.addLog("error", `Failed to approve consultation: ${error.message}`);
        this.showSnackbar("Failed to approve consultation", "error");
      }
    },

    async updateConsultationStatus(consultationId, action) {
      try {
        await axios.post("/consultation_billings/update_consultation", {
          consultation_id: consultationId,
          action: action
        });
        this.addLog(
          "success",
          `Updated consultation ${consultationId}: ${action}`
        );
        this.showSnackbar(`Consultation ${action} successful`, "success");
        // Refresh pending consultations to show updated status
        this.fetchPendingConsultations();
      } catch (error) {
        this.addLog("error", `Failed to update consultation: ${error.message}`);
        this.showSnackbar("Failed to update consultation", "error");
      }
    },

    async startBillingForConsultation(consultationId) {
      try {
        await axios.post("/consultation_billings/start", {
          consultation_id: consultationId
        });
        this.addLog(
          "success",
          `Started billing for consultation ${consultationId}`
        );
        this.showSnackbar("Billing started successfully", "success");
        // Refresh both lists since this consultation will move from pending to active
        this.fetchPendingConsultations();
        this.fetchActiveBillings();
      } catch (error) {
        this.addLog("error", `Failed to start billing: ${error.message}`);
        this.showSnackbar("Failed to start billing", "error");
      }
    },

    // Utility methods
    setupRefreshInterval() {
      this.refreshInterval = setInterval(() => {
        this.fetchActiveBillings();
        this.fetchPendingConsultations();
        this.fetchApprovedConsultations();
      }, 30000); // Refresh every 30 seconds
    },

    addLog(level, message) {
      this.debugLogs.push({
        level,
        message,
        timestamp: new Date(),
      });

      // Keep only last 100 logs
      if (this.debugLogs.length > 100) {
        this.debugLogs.shift();
      }

      if (this.$refs.logsContainer) {
        this.$nextTick(() => {
          this.$refs.logsContainer.scrollTop =
            this.$refs.logsContainer.scrollHeight;
        });
      }
    },

    clearLogs() {
      this.debugLogs = [];
      this.websocketEvents = [];
      this.addLog("info", "Logs cleared");
    },

    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        message,
        color,
      };
    },

    formatTime(timestamp) {
      if (!timestamp) return "Never";
      const date = new Date(timestamp);
      return date.toLocaleTimeString();
    },

    getTimeSince(timestamp) {
      if (!timestamp) return "Never";
      const now = new Date();
      const then = new Date(timestamp);
      const diffSeconds = Math.floor((now - then) / 1000);

      if (diffSeconds < 60) return `${diffSeconds}s ago`;
      if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
      return `${Math.floor(diffSeconds / 3600)}h ago`;
    },

    getTimeSinceColor(timestamp) {
      if (!timestamp) return "grey";
      const now = new Date();
      const then = new Date(timestamp);
      const diffSeconds = Math.floor((now - then) / 1000);

      if (diffSeconds < 120) return "green"; // < 2 min
      if (diffSeconds < 300) return "orange"; // < 5 min
      return "red"; // > 5 min
    },

    getEventColor(type) {
      switch (type) {
        case "billing.minute.processed":
          return "green";
        case "billing.session.stopped":
          return "red";
        case "billing.insufficient.funds":
          return "orange";
        default:
          return "blue";
      }
    },

    getEventIcon(type) {
      switch (type) {
        case "billing.minute.processed":
          return "mdi-cash";
        case "billing.session.stopped":
          return "mdi-stop";
        case "billing.insufficient.funds":
          return "mdi-alert";
        default:
          return "mdi-information";
      }
    },

    getLogColor(level) {
      switch (level) {
        case "error":
          return "red--text";
        case "warning":
          return "orange--text";
        case "success":
          return "green--text";
        case "info":
          return "blue--text";
        default:
          return "grey--text";
      }
    },

    downloadLogs() {
      const logs = this.debugLogs
        .map(
          (log) =>
            `[${log.timestamp.toISOString()}] [${log.level.toUpperCase()}] ${
              log.message
            }`
        )
        .join("\n");

      const blob = new Blob([logs], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `billing-debug-logs-${new Date().toISOString()}.txt`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.font-weight-mono {
  font-family: "Courier New", monospace;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>